/**
 * Crosshair dot — brightens when the camera looks at an interactable
 * (mesh carrying `userData.interactId`, per the frozen userData contract)
 * within interact range. UI raycasts from ctx.camera against ctx.scene since
 * no hover/highlight event exists in the vocabulary. Owned by UI.
 */
import { isTouchDevice } from '../core/platform'
import { Raycaster, Vector2 } from 'three'
import type { Object3D } from 'three'
import type { ModuleContext } from '../core/types'
import {
  CROSSHAIR_CHECK_INTERVAL_S,
  HIDDEN_CLASS,
  INTERACT_PROMPT_POINTER,
  INTERACT_PROMPT_TOUCH,
  INTERACT_RANGE_M
} from './constants'
import { el } from './dom'

const HOT_CLASS = 'gum-crosshair-hot'
const NDC_CENTER = new Vector2(0, 0)
const INTERACT_ID_KEY = 'interactId'

export interface Crosshair {
  setVisible(visible: boolean): void
}

const hasInteractId = (object: Object3D): boolean => {
  let node: Object3D | null = object
  while (node !== null) {
    if (typeof node.userData[INTERACT_ID_KEY] === 'string') return true
    node = node.parent
  }
  return false
}

export const createCrosshair = (root: HTMLElement, ctx: ModuleContext): Crosshair => {
  const dot = el('div', `gum-crosshair ${HIDDEN_CLASS}`)
  const promptText = isTouchDevice() ? INTERACT_PROMPT_TOUCH : INTERACT_PROMPT_POINTER
  const prompt = el('div', `gum-interact-prompt ${HIDDEN_CLASS}`, promptText)
  root.append(dot, prompt)

  const raycaster = new Raycaster()
  raycaster.far = INTERACT_RANGE_M

  let visible = false
  let sinceCheckS = 0

  const setHot = (hot: boolean): void => {
    dot.classList.toggle(HOT_CLASS, hot)
    prompt.classList.toggle(HIDDEN_CLASS, !hot)
  }

  ctx.loop.onUpdate((dt) => {
    if (!visible) return
    sinceCheckS += dt
    if (sinceCheckS < CROSSHAIR_CHECK_INTERVAL_S) return
    sinceCheckS = 0
    raycaster.setFromCamera(NDC_CENTER, ctx.camera)
    const hits = raycaster.intersectObjects(ctx.scene.children, true)
    setHot(hits.some((hit) => hit.distance <= INTERACT_RANGE_M && hasInteractId(hit.object)))
  })

  return {
    setVisible: (next) => {
      visible = next
      dot.classList.toggle(HIDDEN_CLASS, !next)
      if (!next) setHot(false)
    }
  }
}
